import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, Empty, Row, Select, Space, Switch,
  Statistic, Table, Tag, Typography, message
} from 'antd';
import {
  ReloadOutlined, PrinterOutlined, DownloadOutlined,
  CheckCircleTwoTone, CloseCircleTwoTone
} from '@ant-design/icons';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import { accountReportsApi } from './accountReportsApi';
import {
  ACCOUNT_TYPES, ACCOUNT_TYPE_LABEL, ACCOUNT_TYPE_COLOR, formatINR
} from './accountReportsConstants';
// Stage 6B.9F — friendly error / empty message helpers.
import {
  friendlyAccountsErrorMessage, friendlyAccountsEmptyMessage
} from '../accountsPageState';
import '../accounts.css';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Trial Balance page (Stage 6B.5).
//
// Reads `ledger_accounts.current_balance` (the value maintained by
// the Stage 6B.4 posting engine). Splits each ledger's balance into
// the rendered debit/credit pair by sign and shows running totals at
// the page header (Total Debit / Total Credit / Difference / Balanced
// indicator).
//
// Filters: asOfDate (informational — the report is always "as of now"
// in Stage 6B.5; period-cut snapshots need a ledger_balance_history
// table that is out of scope), accountType, includeZero.
//
// Reconciliation: the backend opportunistically attaches an advisory
// `meta.reconciliation` blob from `reconcileLedgerBalances`. If a
// mismatch is detected we surface a yellow banner; we never mutate
// or auto-fix from the UI.
// ─────────────────────────────────────────────────────────────────

function csvSafe(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, rows, columns) {
  const header = columns.map((c) => csvSafe(c.title)).join(',');
  const body = rows.map((r) =>
    columns.map((c) => csvSafe(typeof c.value === 'function' ? c.value(r) : r[c.dataIndex])).join(',')
  ).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function TrialBalancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Stage 6B.9F — preserve the last failure so the page can render an
  // Alert + Retry button instead of just flashing a toast.
  const [loadError, setLoadError] = useState(null);
  const [accountType, setAccountType] = useState(null);
  const [includeZero, setIncludeZero] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const out = await accountReportsApi.trialBalance({
        accountType, includeZero: includeZero ? 'true' : 'false'
      });
      setData(out);
    } catch (e) {
      setLoadError(e);
      message.error(friendlyAccountsErrorMessage(e, 'trial_balance'));
    } finally { setLoading(false); }
  }, [accountType, includeZero]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const rows = data?.rows || [];
  const summary = data?.summary || { totalDebit: 0, totalCredit: 0, difference: 0, isBalanced: true, ledgerCount: 0 };
  const recon = data?.meta?.reconciliation;
  const mismatchCount = recon?.mismatchCount || 0;

  const columns = useMemo(() => ([
    { title: 'Code', dataIndex: 'accountCode', key: 'accountCode', width: 110,
      render: (v) => <code style={{ fontSize: 12 }}>{v}</code> },
    { title: 'Account Name', dataIndex: 'accountName', key: 'accountName' },
    { title: 'Type', dataIndex: 'accountType', key: 'accountType', width: 110,
      render: (v) => (
        <Tag color={ACCOUNT_TYPE_COLOR[v] || 'default'} style={{ marginInlineEnd: 0 }}>
          {ACCOUNT_TYPE_LABEL[v] || v}
        </Tag>
      ) },
    { title: 'Group', dataIndex: 'accountGroup', key: 'accountGroup', width: 160,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Debit (₹)', dataIndex: 'debitAmount', key: 'debitAmount', width: 140, align: 'right',
      render: (v) => v > 0 ? formatINR(v) : <Text type="secondary">—</Text> },
    { title: 'Credit (₹)', dataIndex: 'creditAmount', key: 'creditAmount', width: 140, align: 'right',
      render: (v) => v > 0 ? formatINR(v) : <Text type="secondary">—</Text> }
  ]), []);

  const csvColumns = useMemo(() => ([
    { title: 'Code', dataIndex: 'accountCode' },
    { title: 'Account Name', dataIndex: 'accountName' },
    { title: 'Type', dataIndex: 'accountType' },
    { title: 'Group', dataIndex: 'accountGroup' },
    { title: 'Debit', dataIndex: 'debitAmount' },
    { title: 'Credit', dataIndex: 'creditAmount' }
  ]), []);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <Title level={4} style={{ margin: 0 }}>Trial Balance</Title>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport} loading={loading}>Refresh</Button>
          <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
          <Button size="small" icon={<DownloadOutlined />}
            disabled={!rows.length}
            onClick={() => downloadCsv('trial-balance.csv', rows, csvColumns)}>
            Export CSV
          </Button>
        </Space>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
        Live debit/credit balances per ledger. Difference must be zero for the books to be in balance.
      </div>

      {/* Stage 6B.9F — error banner with retry. Persists until the
          next fetch succeeds so the user doesn't lose context after
          a toast fades. */}
      {loadError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 10 }}
          message="Could not load Trial Balance"
          description={friendlyAccountsErrorMessage(loadError, 'trial_balance')}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchReport}>
              Retry
            </Button>
          }
        />
      ) : null}

      {recon && recon.checked && recon.matched === false ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 10 }}
          message={`Ledger balance mismatch detected on ${mismatchCount} ${mismatchCount === 1 ? 'ledger' : 'ledgers'}.`}
          description="Run the accounting diagnostics before relying on these figures. The Trial Balance is read directly from `ledger_accounts.current_balance` — a mismatch with posted voucher lines indicates a drift that should be investigated, not auto-fixed."
        />
      ) : null}

      {/* Header cards */}
      <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Total Debit (₹)" value={summary.totalDebit} precision={2} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Total Credit (₹)" value={summary.totalCredit} precision={2} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Difference (₹)" value={Math.abs(summary.difference)}
              precision={2} valueStyle={{ color: summary.isBalanced ? '#3f8600' : '#cf1322' }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <div style={{ fontSize: 11.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.2, fontWeight: 500 }}>Status</div>
            <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4, color: '#0f172a' }}>
              {summary.isBalanced ? (
                <span><CheckCircleTwoTone twoToneColor="#52c41a" /> Balanced</span>
              ) : (
                <span><CloseCircleTwoTone twoToneColor="#cf1322" /> Not Balanced</span>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>
              {summary.ledgerCount} ledger{summary.ledgerCount === 1 ? '' : 's'}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Filters — Stage 6B.7C: the disabled "As of date" placeholder
          has been removed. The Trial Balance is a LIVE balance read
          today; a snapshot-as-of-date filter will be re-introduced
          once `ledger_balance_history` lands in a later stage. */}
      <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
        <Space wrap>
          <Space>
            <Text>Account type:</Text>
            <Select size="small"
              allowClear placeholder="All types" style={{ width: 160 }}
              value={accountType}
              onChange={(v) => setAccountType(v || null)}
              options={ACCOUNT_TYPES.map((t) => ({ value: t, label: ACCOUNT_TYPE_LABEL[t] }))}
            />
          </Space>
          <Space>
            <Text>Include zero balances:</Text>
            <Switch size="small" checked={includeZero} onChange={setIncludeZero} />
          </Space>
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            Showing live balances as of now. Period-cut snapshots arrive with the ledger history table.
          </Text>
        </Space>
      </Card>

      <Table
        size="small"
        rowKey="accountId"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 50, size: 'small', showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '200'] }}
        locale={{ emptyText:
          <Empty description={friendlyAccountsEmptyMessage('trial_balance')} /> }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4}>Total</Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">₹ {formatINR(summary.totalDebit)}</Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">₹ {formatINR(summary.totalCredit)}</Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      </div>
    </ErpMasterShell>
  );
}
